import { ASYNC_STATUS_ID } from 'constants/index'
import { API } from 'constants/apiEndPoints'
import { atom, useAtomValue, useSetAtom } from 'jotai'
import { useEffect, useState } from 'react'
import { useAPI } from 'utils/hooks/useAPI'
import { useSocketListener } from './sockets/useSocket'

type TAssetVariation = {
  symbol: string
  percentageChange: string
}

type TAssetVariationCache = TAssetVariation[]

const assetVariationCacheAtom = atom<TAssetVariationCache>([])

const getAssetsWithoutVariation = (assets: TBaseAsset[], assetsVariation: TAssetVariation[]): TExtendedAsset[] => {
  const assetsWithoutVariation = assets
    .filter((asset) => {
      const assetVariation = assetsVariation.find((assetVariation) => assetVariation.symbol === asset.id)
      return !assetVariation
    })
    .map((asset) => ({ ...asset, symbol: asset.id, percentageChange: '0' }))
  return assetsWithoutVariation
}

const sortAssetWithVariation = (assets: TBaseAsset[], assetVariation: TAssetVariation[]): TExtendedAsset[] => {
  const sortedAssets = assetVariation
    .map((assetVariation) => {
      const asset = assets.find((asset) => assetVariation.symbol === asset.id)
      if (!asset) 
        return null
      
      return { ...asset, ...assetVariation }
    })
    .filter((asset) => asset !== null) as TExtendedAsset[]
  return sortedAssets
}

const updateAssetWithVariation = (assets: TBaseAsset[], assetsVariation: TAssetVariation[]) => {
  const sortedAssets = sortAssetWithVariation(assets, assetsVariation)
  const assetsWithoutVariation = getAssetsWithoutVariation(assets, assetsVariation)
  return [...sortedAssets, ...assetsWithoutVariation]
}

export const useAssetVariationOnLoad = () => {
  const setAssetVariationCache = useSetAtom(assetVariationCacheAtom)
  const [assetChangeStatus, sendAssetChangeQuery, hasSendAssetStatus] = useAPI(API.ASSETS_DAILY_CHANGE, {
    queryType: 'get',
    controlled: true,
    withJwt: false,
  })
  useEffect(() => {
    sendAssetChangeQuery()
  }, [])

  useEffect(() => {
    if (hasSendAssetStatus(ASYNC_STATUS_ID.ERROR)) {
    }
    if (hasSendAssetStatus(ASYNC_STATUS_ID.CONFIRMED)) {
      const { array } = assetChangeStatus.data
      setAssetVariationCache(array)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assetChangeStatus.status?.id])
}

export const useAssetsWithVariation = (assets: TBaseAsset[]): { assetWithVariation: TExtendedAsset[] } => {
  const assetVariationCache = useAtomValue(assetVariationCacheAtom)
  const [assetWithVariation, setAssetWithVariation] = useState<TExtendedAsset[]>([])

  useEffect(() => {
    const assetsWithVariation = updateAssetWithVariation(assets, assetVariationCache)
    setAssetWithVariation(assetsWithVariation)
  }, [assets, assetVariationCache])

  useSocketListener('gateway','asset.public', (assetsVariation: TAssetVariation[]) => {
    const assetsWithVariation = updateAssetWithVariation(assets, assetsVariation)
    setAssetWithVariation(assetsWithVariation)
  })

  return { assetWithVariation }
}
